import {
  Cartesian3,
  VerticalOrigin,
  JulianDate,
  Color,
  Cartographic,
  Math as CesiumMath,
  BillboardCollection,
  Ellipsoid,
  EllipsoidGeodesic,
  Cartesian2,
  WebMercatorProjection,
  Rectangle,
  SceneTransforms,
} from "cesium";

/**
 * Arrows: every 5 m at all zooms.
 * Debounced rebuild: 1s after integer zoom/height or camera move changes.
 * Only entities in current view.
 * Screen de-clutter: min 20 px separation between arrows (configurable).
 */
export const toggleDirectionalArrows = (viewer, dataSource, show = true, directionArrowSettingRef) => {
  if (!viewer || !dataSource || !viewer.scene?.primitives) return;

  const scene = viewer.scene;
  const primitives = scene.primitives;

  // Billboard collection
  if (!dataSource._arrowBillboards) {
    dataSource._arrowBillboards = primitives.add(new BillboardCollection());
  }
  const billboardCollection = dataSource._arrowBillboards;

  // Cleanup
  if (!show) {
    billboardCollection.removeAll();
    if (dataSource._arrowCameraListener) {
      viewer.camera.changed.removeEventListener(dataSource._arrowCameraListener);
      dataSource._arrowCameraListener = null;
    }
    if (dataSource._arrowBuildTimer) {
      clearTimeout(dataSource._arrowBuildTimer);
      dataSource._arrowBuildTimer = null;
    }
    // visibility sync cleanup
    if (dataSource._arrowVisibilityListener) {
      scene.postRender.removeEventListener(dataSource._arrowVisibilityListener);
      dataSource._arrowVisibilityListener = null;
    }
    dataSource._arrowBillboardsByEntity = null;
    dataSource._arrowLastDsShow = undefined;

    dataSource._arrowBillboards = null;
    dataSource._lastAppliedKey = undefined;
    return;
  }

  /* ---------------- CONFIG ---------------- */
  const ARROW_IMAGE_URL = "assets/image/arrow2.png";
  const BASE_SCALE = Number(directionArrowSettingRef?.current?.scale ?? 1);
  const COLOR_CSS = directionArrowSettingRef?.current?.color ?? "#ff3b30";
  const MIN_SEP_PX = Number(directionArrowSettingRef?.current?.minSepPx ?? 120); // 20px separation

  /* ---------------- HELPERS ---------------- */
  const isFiniteCartesian3 = (c) => c && Number.isFinite(c.x) && Number.isFinite(c.y) && Number.isFinite(c.z);

  const getShowValue = (v, time) => {
    if (v == null) return true;
    if (typeof v === "boolean") return v;
    if (typeof v.getValue === "function") return !!v.getValue(time);
    return true;
  };

  const bearingFromCartos = (c1, c2) => {
    const dLon = c2.longitude - c1.longitude;
    const y = Math.sin(dLon) * Math.cos(c2.latitude);
    const x = Math.cos(c1.latitude) * Math.sin(c2.latitude) - Math.sin(c1.latitude) * Math.cos(c2.latitude) * Math.cos(dLon);
    return Math.atan2(y, x);
  };

  const bearingToBillboardRotation = (bearingRad) => CesiumMath.negativePiToPi(CesiumMath.PI_OVER_TWO - bearingRad);

  const computeZoomLevel = () => {
    const camera = viewer.camera;
    const canvas = viewer.canvas;
    const w = canvas.clientWidth || canvas.width;
    const h = canvas.clientHeight || canvas.height;
    const y = h / 2;

    const left = camera.pickEllipsoid(new Cartesian2(0, y), scene.globe.ellipsoid);
    const right = camera.pickEllipsoid(new Cartesian2(w, y), scene.globe.ellipsoid);

    let mpp;
    if (left && right) {
      mpp = Cartesian3.distance(left, right) / w;
    } else {
      const height = camera.positionCartographic?.height ?? 1;
      const fovy = camera.frustum.fovy || CesiumMath.toRadians(60);
      mpp = (2 * height * Math.tan(fovy / 2)) / h;
    }

    const centerCarto = viewer.camera.positionCartographic || { latitude: 0 };
    const cosLat = Math.cos(centerCarto.latitude);
    const R = new WebMercatorProjection().ellipsoid.maximumRadius;
    const tileSize = 256;
    return Math.log2((cosLat * 2 * Math.PI * R) / (mpp * tileSize));
  };

  const getViewRectangle = () => {
    let rect = viewer.camera.computeViewRectangle(scene.globe.ellipsoid);
    if (rect) return rect;

    const canvas = viewer.canvas;
    const w = canvas.clientWidth || canvas.width;
    const h = canvas.clientHeight || canvas.height;
    const pts = [new Cartesian2(0, 0), new Cartesian2(w, 0), new Cartesian2(0, h), new Cartesian2(w, h)]
      .map((p) => viewer.camera.pickEllipsoid(p, scene.globe.ellipsoid))
      .filter(Boolean)
      .map((c) => Cartographic.fromCartesian(c));

    if (pts.length === 0) return undefined;
    return Rectangle.fromCartographicArray(pts);
  };

  const cartoInRect = (carto, rect) => rect && Rectangle.contains(rect, carto);

  const getEntityPositions = (entity) => {
    if (entity.polyline?.positions) {
      const raw = entity.polyline.positions.getValue ? entity.polyline.positions.getValue(JulianDate.now()) : entity.polyline.positions;
      const positions = (raw || []).filter(isFiniteCartesian3);
      return positions?.length >= 2 ? positions : null;
    }
    if (entity.polygon?.hierarchy?.getValue) {
      const hierarchy = entity.polygon.hierarchy.getValue(JulianDate.now());
      if (hierarchy?.positions?.length >= 3) {
        const arr = hierarchy.positions.slice();
        if (!Cartesian3.equals(arr[0], arr[arr.length - 1])) arr.push(arr[0]);
        return arr.filter(isFiniteCartesian3);
      }
    }
    return null;
  };

  // IMPORTANT: visibility gating (entity/dataSource hidden => no arrows)
  const isEntityVisibleNow = (entity, time) => {
    if (dataSource.show === false) return false;

    // honor entity show/parent/availability if available
    const baseEntityShow = typeof entity?.isShowing === "boolean" ? entity.isShowing : getShowValue(entity?.show, time);
    if (!baseEntityShow) return false;

    // honor geometry-level show when applicable
    if (entity?.polyline?.positions) return getShowValue(entity.polyline.show, time);
    if (entity?.polygon?.hierarchy) return getShowValue(entity.polygon.show, time);

    return true;
  };

  const buildSegments = (positions) => {
    const segments = [];
    let totalLength = 0;
    for (let i = 0; i < positions.length - 1; i++) {
      const c1 = Cartographic.fromCartesian(positions[i]);
      const c2 = Cartographic.fromCartesian(positions[i + 1]);
      if (!c1 || !c2) continue;
      c1.height = 0;
      c2.height = 0;
      const g = new EllipsoidGeodesic(c1, c2);
      const segLen = g.surfaceDistance || 0;
      if (segLen > 0) {
        segments.push({ geodesic: g, length: segLen });
        totalLength += segLen;
      }
    }
    return { segments, totalLength };
  };

  const entityCenterCartographic = (positions) => {
    if (!positions || positions.length < 2) return null;

    let segs = [];
    let total = 0;
    for (let i = 0; i < positions.length - 1; i++) {
      const c1 = Cartographic.fromCartesian(positions[i]);
      const c2 = Cartographic.fromCartesian(positions[i + 1]);
      if (!c1 || !c2) continue;
      c1.height = 0;
      c2.height = 0;
      const g = new EllipsoidGeodesic(c1, c2);
      const L = g.surfaceDistance || 0;
      if (L > 0) {
        segs.push({ g, L });
        total += L;
      }
    }
    if (total === 0) return null;

    const mid = total / 2;
    let acc = 0;
    for (const s of segs) {
      if (acc + s.L >= mid) {
        const f = (mid - acc) / s.L;
        return s.g.interpolateUsingFraction(f);
      }
      acc += s.L;
    }
    return segs[segs.length - 1].g.interpolateUsingFraction(1);
  };

  const entityIsInView = (entity, rect) => {
    const positions = getEntityPositions(entity);
    if (!positions) return false;

    for (const p of positions) {
      const c = Cartographic.fromCartesian(p);
      if (cartoInRect(c, rect)) return true;
    }

    const center = entityCenterCartographic(positions);
    return center ? cartoInRect(center, rect) : false;
  };

  /* ---------------- VISIBILITY SYNC (immediate hide on entity/dataSource hide) ---------------- */
  const ensureVisibilitySync = () => {
    if (dataSource._arrowVisibilityListener) return;

    dataSource._arrowLastDsShow = dataSource.show !== false;
    dataSource._arrowLastVisCheckMs = 0;

    const listener = () => {
      // throttle: keep it light
      const now = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
      if (now - (dataSource._arrowLastVisCheckMs || 0) < 150) return;
      dataSource._arrowLastVisCheckMs = now;

      if (!dataSource._arrowBillboards) return;

      const dsVisible = dataSource.show !== false;
      if (dsVisible !== dataSource._arrowLastDsShow) {
        billboardCollection.show = dsVisible;
        dataSource._arrowLastDsShow = dsVisible;
      } else {
        // keep in sync even if someone sets billboardCollection.show elsewhere
        billboardCollection.show = dsVisible;
      }

      if (!dsVisible) return;

      const groups = dataSource._arrowBillboardsByEntity;
      if (!groups) return;

      const t = JulianDate.now();
      for (const [, g] of groups) {
        const v = isEntityVisibleNow(g.entity, t);
        if (v === g.lastVisible) continue;
        g.lastVisible = v;
        const arr = g.billboards;
        for (let i = 0; i < arr.length; i++) {
          arr[i].show = v;
        }
      }
    };

    scene.postRender.addEventListener(listener);
    dataSource._arrowVisibilityListener = listener;
  };

  /* ---------------- CORE BUILD ---------------- */
  const rebuild = () => {
    const rect = getViewRectangle();
    billboardCollection.removeAll();

    // reset grouping map used for visibility sync
    dataSource._arrowBillboardsByEntity = new Map();

    // datasource hidden => no arrows at all
    billboardCollection.show = dataSource.show !== false;
    if (dataSource.show === false) return;

    if (!rect) return;

    // compute zoom once
    const z = Number.isFinite(computeZoomLevel()) ? computeZoomLevel() : 0;
    const entities = dataSource.entities.values || [];

    // min separation squared
    const minSepSq = MIN_SEP_PX * MIN_SEP_PX;

    /* ---------------- screen de-clutter helpers ---------------- */
    // global spacing across all entities (old behavior)
    const globalScreenPts = [];
    // Convert cartographic to window and check global separation
    const canPlaceGlobal = (carto) => {
      // convert and early exit
      const pos = Ellipsoid.WGS84.cartographicToCartesian(carto);
      const win = SceneTransforms.worldToWindowCoordinates(scene, pos);
      if (!win) return false;
      for (let i = 0; i < globalScreenPts.length; i++) {
        const p = globalScreenPts[i];
        const dx = win.x - p.x;
        const dy = win.y - p.y;
        if (dx * dx + dy * dy < minSepSq) return false;
      }
      globalScreenPts.push(new Cartesian2(win.x, win.y));
      return true;
    };

    // per-entity spacing (new behavior for zoom >= 20)
    const screenPtsByEntity = new Map();
    const canPlacePerEntity = (carto, entityId) => {
      const pos = Ellipsoid.WGS84.cartographicToCartesian(carto);
      const win = SceneTransforms.worldToWindowCoordinates(scene, pos);
      if (!win) return false;
      if (!screenPtsByEntity.has(entityId)) screenPtsByEntity.set(entityId, []);
      const pts = screenPtsByEntity.get(entityId);
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        const dx = win.x - p.x;
        const dy = win.y - p.y;
        if (dx * dx + dy * dy < minSepSq) return false;
      }
      pts.push(new Cartesian2(win.x, win.y));
      return true;
    };

    /* ---------------- fixed spacing = 0.3 m everywhere ---------------- */
    const spacing = 0.3;

    for (let ei = 0; ei < entities.length; ei++) {
      const entity = entities[ei];

      // FIX: entity hidden => skip entirely
      if (!isEntityVisibleNow(entity, JulianDate.now())) continue;

      const positions = getEntityPositions(entity);
      if (!positions) continue;
      if (z <= 25 && !entityIsInView(entity, rect)) continue;

      const { segments, totalLength } = buildSegments(positions);
      if (!segments.length || totalLength <= 0) continue;

      // choose placement checker based on zoom threshold
      const usePerEntity = z >= 17;

      // stable id for per-entity map
      const eid = entity.id != null ? String(entity.id) : `__ent_${ei}`;
      if (usePerEntity && !screenPtsByEntity.has(eid)) screenPtsByEntity.set(eid, []);

      // init group for visibility sync
      if (!dataSource._arrowBillboardsByEntity.has(eid)) {
        dataSource._arrowBillboardsByEntity.set(eid, { entity, billboards: [], lastVisible: true });
      }
      const group = dataSource._arrowBillboardsByEntity.get(eid);

      const addBillboard = (carto, aheadCarto) => {
        const pos = Ellipsoid.WGS84.cartographicToCartesian(carto);
        const bearing = bearingFromCartos(carto, aheadCarto || carto);
        const rotation = bearingToBillboardRotation(bearing);
        const bb = billboardCollection.add({
          position: pos,
          image: ARROW_IMAGE_URL,
          scale: BASE_SCALE,
          color: Color.fromCssColorString(COLOR_CSS).withAlpha(1.0),
          rotation,
          verticalOrigin: VerticalOrigin.CENTER,
          alignedAxis: Cartesian3.ZERO,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          show: true,
        });
        group.billboards.push(bb);
      };

      const count = Math.max(1, Math.floor(totalLength / spacing));
      for (let i = 0; i < count; i++) {
        const fAlong = (i + 1) / (count + 1);
        let acc = 0;
        for (let s = 0; s < segments.length; s++) {
          const nextAcc = acc + segments[s].length;
          if (fAlong * totalLength <= nextAcc) {
            const seg = segments[s];
            const fracInSeg = (fAlong * totalLength - acc) / seg.length;
            const cartoAt = seg.geodesic.interpolateUsingFraction(fracInSeg);
            const cartoAhead = seg.geodesic.interpolateUsingFraction(Math.min(fracInSeg + 0.01, 1));
            if (!cartoInRect(cartoAt, rect)) break;

            // enforce separation: choose appropriate checker
            const ok = usePerEntity ? canPlacePerEntity(cartoAt, eid) : canPlaceGlobal(cartoAt);
            if (ok) addBillboard(cartoAt, cartoAhead);
            break;
          }
          acc = nextAcc;
        }
      }
    }
  };

  /* ---------------- DEBOUNCED CAMERA LISTENER ---------------- */
  // Replace the whole cameraKey() with this ultra-sensitive version
  const cameraKey = () => {
    const z = computeZoomLevel(); // zoom as float
    const pc = viewer.camera.positionCartographic || Cartographic.ZERO;

    // Use high precision so sub-meter and sub-pixel shifts change the key
    const zStr = Number.isFinite(z) ? z.toPrecision(15) : "0";
    const hStr = (pc.height ?? 0).toPrecision(15); // meters
    const latStr = (pc.latitude ?? 0).toPrecision(15); // radians
    const lonStr = (pc.longitude ?? 0).toPrecision(15); // radians

    // If you also want rotation sensitivity, append heading/pitch/roll:
    const hd = viewer.camera.heading.toPrecision(15);
    const pt = viewer.camera.pitch.toPrecision(15);
    const rl = viewer.camera.roll.toPrecision(15);
    return `${zStr}|${hStr}|${latStr}|${lonStr}|${hd}|${pt}|${rl}`;

    // return `${zStr}|${hStr}|${latStr}|${lonStr}`;
  };

  // first draw
  rebuild();
  ensureVisibilitySync();
  dataSource._lastAppliedKey = cameraKey();

  const onCameraChanged = () => {
    const keyNow = cameraKey();
    if (keyNow === dataSource._queuedKey) return;
    dataSource._queuedKey = keyNow;

    if (dataSource._arrowBuildTimer) {
      clearTimeout(dataSource._arrowBuildTimer);
    }
    dataSource._arrowBuildTimer = setTimeout(() => {
      const freshKey = cameraKey();
      if (freshKey !== dataSource._lastAppliedKey) {
        rebuild();
        dataSource._lastAppliedKey = freshKey;
      }
      dataSource._arrowBuildTimer = null;
    }, 1000);
  };

  // attach
  if (dataSource._arrowCameraListener) {
    viewer.camera.changed.removeEventListener(dataSource._arrowCameraListener);
  }
  viewer.camera.changed.addEventListener(onCameraChanged);
  dataSource._arrowCameraListener = onCameraChanged;
};
